const BLUEPRINT_EXPORT_CSS = `
  :root {
    --navy: #0f172a;
    --blue: #2e5aac;
    --teal: #0d9488;
    --muted: #64748b;
    --line: #e2e8f0;
    --panel: #f8fafc;
    --card-bg: #ffffff;
    --text: #334155;
    --warning-bg: #fffbeb;
    --warning-text: #92400e;
  }

  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Arial, Helvetica, sans-serif;
    background: #f4f7fb;
    color: var(--text);
  }
  .export-page {
    max-width: 1190px;
    margin: 24px auto;
    padding: 0 20px 40px;
  }
  .blueprint-view {
    max-width: 1190px;
    margin: 0 auto;
  }
  .blueprint-card {
    background: var(--card-bg);
    border: 1px solid var(--line);
    border-radius: 16px;
    padding: 28px 32px 36px;
    box-shadow: 0 4px 24px rgba(15, 23, 42, 0.04);
  }
  .blueprint-card > h2 {
    margin: 0 0 4px;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--navy);
  }
  .blueprint-sub {
    font-size: 0.875rem;
    color: var(--muted);
    margin-bottom: 18px;
  }
  .badge-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 28px;
  }
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 999px;
    background: #f1f5f9;
    color: #334155;
    font-size: 0.75rem;
    font-weight: 500;
    border: 1px solid var(--line);
  }
  .bp-sec {
    background: var(--card-bg);
    border: 1px solid #e8edf5;
    border-radius: 12px;
    padding: 0;
    margin-bottom: 14px;
    overflow: hidden;
  }
  .bp-sec-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background: #f4f7fd;
    padding: 10px 16px;
    font-weight: 700;
    color: var(--navy);
    font-size: 13px;
    border-bottom: 1px solid #e8edf5;
  }
  .bp-sec-head > span:first-child {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1e293b;
  }
  .bp-sec-head .tag {
    font-size: 10px;
    background: #e2ebf9;
    color: var(--blue);
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 600;
    white-space: nowrap;
  }
  .bp-sec-body {
    padding: 14px 16px 16px;
    font-size: 0.9rem;
    color: var(--text);
    line-height: 1.6;
  }
  .bp-sec-body ul {
    margin: 0;
    padding-left: 18px;
  }
  .bp-sec-body li {
    margin-bottom: 6px;
  }
  .bp-sec-body li b,
  .bp-sec-body b {
    color: #0f172a;
    font-weight: 600;
  }
  .why {
    margin-top: 12px;
    padding: 11px 14px;
    background: var(--warning-bg);
    border: 1px solid #fde68a;
    border-radius: 8px;
    font-size: 0.85rem;
    color: var(--warning-text);
    line-height: 1.5;
  }
  .muted {
    color: #94a3b8 !important;
    font-weight: 400;
  }
  .timeline {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
  }
  .timeline-phase {
    display: grid;
    grid-template-columns: 150px 1fr;
    align-items: center;
    gap: 14px;
  }
  .timeline-label {
    font-size: 0.84rem;
    color: #475569;
    text-align: left;
    line-height: 1.3;
  }
  .timeline-bar {
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #ffffff;
    min-width: 56px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }
  .hint {
    margin-top: 14px;
    font-size: 0.82rem;
    color: var(--muted);
  }
`;

export function buildBlueprintHtmlDocument(bodyHtml, title = 'SpringUp Blueprint Proposal') {
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${title}</title>
    <style>${BLUEPRINT_EXPORT_CSS}</style>
  </head>
  <body>
    <div class="export-page">${bodyHtml}</div>
  </body>
</html>`;
}

export function exportBlueprintAsHtml(elementOrHtml, filename = 'blueprint.html') {
  const source = typeof elementOrHtml === 'string' ? elementOrHtml : elementOrHtml.outerHTML || elementOrHtml.innerHTML;
  const html = buildBlueprintHtmlDocument(source, 'SpringUp Blueprint Proposal');
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Simple text-only PDF (no cropping, no blank pages)
 */
export async function exportBlueprintAsPdf(text, filename = 'blueprint.pdf') {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  const margin = 40;
  const maxWidth = 515; // A4 width 595 - 2*40
  const lineHeight = 14;
  let y = margin;

  const lines = doc.splitTextToSize(text, maxWidth);

  lines.forEach((line) => {
    if (y > 800) {               // leave a little bottom margin
      doc.addPage();
      y = margin;
    }
    doc.text(line, margin, y);
    y += lineHeight;
  });

  doc.save(filename);
}

/**
 * High-quality visual PDF – fixed version
 * - Prevents text cropping
 * - Prevents hundreds of blank pages
 * - Only generates pages that actually contain content
 */
/**
 * High-quality visual PDF – FIXED (much less text cutting)
 */
export async function exportBlueprintAsPdfClient(elementOrHtml, filename = 'SpringUp_Blueprint.pdf') {
  const { jsPDF } = await import('jspdf');
  const html2canvas = (await import('html2canvas')).default;

  // ── 1. Prepare clean clone ──────────────────────────────────────────────
  let source;
  if (typeof elementOrHtml === 'string') {
    const parsed = new DOMParser().parseFromString(elementOrHtml, 'text/html');
    source = parsed.body.firstElementChild || parsed.body;
  } else {
    source = elementOrHtml;
  }
  if (!source) throw new Error('No element to export');

  const clone = source.cloneNode(true);

  // Open all <details>
  clone.querySelectorAll('details').forEach((d) => {
    d.open = true;
    d.setAttribute('open', '');
  });

  // Remove UI chrome
  clone
    .querySelectorAll('.nav-row, .export-menu-container, .export-menu, .btn.export-item')
    .forEach((n) => n.remove());

  try {
    const secs = Array.from(clone.querySelectorAll('.bp-sec'));
    const roleSec = secs.find((s) => {
      const head = s.querySelector('.bp-sec-head span, .bp-sec-head');
      const text = (head?.textContent || '').toUpperCase();
      return text.includes('ROLE PROFILES') || text.includes('3 · ROLE');
    });
    if (roleSec && roleSec.parentNode) {
      roleSec.parentNode.appendChild(roleSec);
    }
  } catch (e) {
    console.warn('Could not reorder ROLE PROFILES section', e);
  }

  // ── 2. Off-screen container (A4 width @ 96 dpi) ─────────────────────────
  const pageWidthPx = 794;
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    left: -9999px;
    top: 0;
    width: ${pageWidthPx}px;
    background: white;
    padding: 0;
    margin: 0;
  `;
  container.appendChild(clone);
  document.body.appendChild(container);

  // ── 3. Styles that help layout + mark non-breakable blocks ──────────────
  const style = document.createElement('style');
  style.textContent = `
    * { box-sizing: border-box !important; }
    body, html { margin: 0 !important; padding: 0 !important; }
 
    .export-page, .blueprint-view, .blueprint-card, .page-shell {
      max-width: 100% !important;
      width: 100% !important;
      overflow: visible !important;
    }
    img, svg { max-width: 100% !important; height: auto !important; }
 
    /* These classes are treated as atomic units by the page-break algorithm */
    .bp-sec,
    .blueprint-role-profile,
    .timeline-phase,
    details,
    .why,
    h2, h3, h4,
    .bp-sec-head {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
 
    .bp-sec-head {
      page-break-after: avoid !important;
      break-after: avoid !important;
    }
 
    .bp-sec-body, p, li, span {
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      line-height: 1.55 !important;
    }
  `;
  clone.prepend(style);

  try {
    // ── 4. Render full content once ───────────────────────────────────────
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      width: pageWidthPx,
      windowWidth: pageWidthPx,
      scrollX: 0,
      scrollY: 0,
      logging: false,
    });

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    // ── 5. PDF geometry (points) ──────────────────────────────────────────
    const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
    const pdfWidth = pdf.internal.pageSize.getWidth();   // ≈ 595.28
    const pdfHeight = pdf.internal.pageSize.getHeight();  // ≈ 841.89

    const marginTop = 36;
    const marginBottom = 48;   // room for page number
    const marginSide = 28;
    const contentWidth = pdfWidth - marginSide * 2;
    const contentHeight = pdfHeight - marginTop - marginBottom;

    const ratio = contentWidth / imgWidth;          // px → pt scale
    const scaledHeight = imgHeight * ratio;

    // ── 6. Collect “do-not-cut” blocks (in canvas pixels) ─────────────────
    // Elements that must stay together
    const avoidSelectors = [
      '.bp-sec',
      '.blueprint-role-profile',
      '.timeline-phase',
      'details',
      '.why',
      'h2', 'h3', 'h4',
      '.bp-sec-head',
    ].join(',');

    const scaleFactor = 2; // same as html2canvas scale
    const blocks = [];

    clone.querySelectorAll(avoidSelectors).forEach((el) => {
      // offset relative to the clone root
      let top = 0;
      let node = el;
      while (node && node !== clone) {
        top += node.offsetTop;
        node = node.offsetParent;
      }
      const height = el.offsetHeight;
      if (height > 0) {
        blocks.push({
          top: top * scaleFactor,
          bottom: (top + height) * scaleFactor,
        });
      }
    });

    // Sort by top
    blocks.sort((a, b) => a.top - b.top);

    // ── 7. Compute safe page-break Y positions (canvas px) ────────────────
    const pageHeightPx = contentHeight / ratio; // how many canvas px fit on one PDF page
    const cuts = [0];                           // start of each page in canvas px
    let y = 0;

    while (y + pageHeightPx < imgHeight - 2) {
      let candidate = y + pageHeightPx;

      // Walk backwards until we are not inside any protected block
      let safe = candidate;
      for (const b of blocks) {
        if (safe > b.top + 4 && safe < b.bottom - 4) {
          // Would cut inside this block → move cut to just before the block
          safe = b.top;
        }
      }

      // Never go backwards past the previous cut
      if (safe <= y + 20) {
        // Block is taller than a page – allow a hard cut (rare)
        safe = candidate;
      }

      cuts.push(safe);
      y = safe;
    }
    cuts.push(imgHeight); // final end

    // ── 8. Draw each page ─────────────────────────────────────────────────
    for (let i = 0; i < cuts.length - 1; i++) {
      if (i > 0) pdf.addPage();

      const srcY = cuts[i];
      const srcH = Math.min(cuts[i + 1] - srcY, imgHeight - srcY);
      if (srcH <= 1) continue;

      // Slice the master canvas
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = imgWidth;
      pageCanvas.height = Math.ceil(srcH);
      const ctx = pageCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(
        canvas,
        0, srcY, imgWidth, srcH,
        0, 0, imgWidth, srcH
      );

      const pageImg = pageCanvas.toDataURL('image/jpeg', 0.93);
      const pageScaledH = srcH * ratio;

      pdf.addImage(pageImg, 'JPEG', marginSide, marginTop, contentWidth, pageScaledH);
    }

    // ── 9. Page numbers (footer) ──────────────────────────────────────────
    const totalPages = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      pdf.setFontSize(9);
      pdf.setTextColor(100);
      pdf.text(
        `SpringUp Blueprint  ·  Page ${p} of ${totalPages}`,
        pdfWidth / 2,
        pdfHeight - 18,
        { align: 'center' }
      );
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Server-side export (unchanged, but now receives clean HTML)
 */
export async function exportBlueprintAsPdfServer(elementOrHtml, filename = 'blueprint.pdf') {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const source = typeof elementOrHtml === 'string'
    ? elementOrHtml
    : elementOrHtml.outerHTML || elementOrHtml.innerHTML;
  const html = buildBlueprintHtmlDocument(source, 'SpringUp Blueprint Proposal');

  const resp = await fetch(`${API_BASE_URL}/proposals/pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ html, filename }),
  });

  if (!resp.ok) {
    const payload = await resp.json().catch(() => ({}));
    throw new Error(payload.message || 'PDF export failed');
  }

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportBlueprintAsDocx(text, filename = 'blueprint.docx') {
  const blob = new Blob([text], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}